import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useReducedMotion } from '../../animations/useReducedMotion';
import HeaderSearch from './HeaderSearch';
import { Moon, Sun, SystemTheme } from './ThemeIcons';

/* Shared color roles keep the terminal chrome in step with both site themes.
   The prompt and active rule carry the green; the title and navigation stay ink. */
interface NavPalette {
    bg: string; inset: string; rule: string; ruleHi: string;
    ink: string; strong: string; muted: string; faint: string; accent: string;
}
const NAV: NavPalette = {
    bg:     'color-mix(in srgb, var(--bs-raised) 90%, transparent)',
    inset:  'var(--bs-sunken)',
    rule:   'var(--bs-rule)',
    ruleHi: 'var(--bs-rule-strong)',
    ink:    'var(--bs-ink)',
    strong: 'var(--bs-ink)',
    muted:  'var(--bs-muted)',
    faint:  'var(--bs-dim)',
    accent: 'var(--bs-accent)',
};

const SITE_TITLE = 'Abhijith on AI';

// Sections that cd navigates to on /about
const CD_ROUTES: Record<string, { path: string; label: string }> = {
    'home': { path: '/', label: 'HOME' },
    'about': { path: '/about', label: 'ABOUT' },
    'articles': { path: '/articles', label: 'ARTICLES' },
    'writing': { path: '/articles', label: 'ARTICLES' },
    'labs': { path: '/labs', label: 'LABS' },
    'radar': { path: '/radar', label: 'RADAR' },
    'fit': { path: '/fit', label: 'FIT' },
    'playground': { path: '/playground', label: 'PLAY' },
    'cinema': { path: '/cinema', label: 'CINEMA' },
    'play': { path: '/playground', label: 'PLAY' },
    'skills': { path: '/about', label: 'skills' },
    'experience': { path: '/about', label: 'experience' },
    'projects': { path: '/about', label: 'projects' },
};

const COMMANDS: Record<string, string> = {
    help: 'Available: ls, whoami, cd <dir>, clear, date, pwd',
    whoami: 'abhijith — Senior Systems Architect & AI Specialist @ Zalando',
    ls: 'HOME/  ARTICLES/  LABS/  CINEMA/  RADAR/  FIT/  PLAY/  ABOUT/  skills/  experience/  projects/',
    clear: '',
    date: new Date().toDateString(),
    pwd: '~/personal',
};

/** The two things the site is built around stay top level. */
const PRIMARY_NAV = [
    { label: 'HOME', path: '/' },
    { label: 'ARTICLES', path: '/articles' },
    { label: 'LABS', path: '/labs' },
];

/** The generated tools, grouped — each carries a line explaining what it is. */
const SECONDARY_NAV = [
    { label: 'CINEMA', path: '/cinema', note: 'eleven years of work, as a flight' },
    { label: 'RADAR', path: '/radar', note: 'what is worth reading this week' },
    { label: 'FIT',   path: '/fit',   note: 'match a job description to the work' },
    { label: 'PLAY',  path: '/playground', note: 'a canvas to poke at' },
];

const TRAILING_NAV = [{ label: 'ABOUT', path: '/about' }];

const navItemStyle = (active: boolean, p: NavPalette): React.CSSProperties => ({
    background: active ? 'var(--bs-accent-soft)' : 'none',
    border: active ? `1px solid ${p.rule}` : '1px solid transparent',
    borderRadius: '999px',
    cursor: 'pointer',
    fontFamily: 'inherit', fontSize: '12px', fontWeight: active ? 600 : 500,
    letterSpacing: '0.05em',
    color: active ? p.accent : p.muted,
    padding: '6px 12px',
    transition: 'color 0.15s, background 0.15s, border-color 0.15s',
    display: 'inline-flex', alignItems: 'center',
    textDecoration: 'none',
});

interface TerminalLine {
    type: 'input' | 'output';
    text: string;
}

type ThemeMode = 'light' | 'dark' | 'system';

interface TerminalHeaderProps {
    mode?: ThemeMode;
    setMode?: (m: ThemeMode) => void;
    toggleTheme?: () => void; // legacy, kept for compat
}

const TerminalHeader: React.FC<TerminalHeaderProps> = ({ mode = 'system', setMode }) => {
    const reducedMotion = useReducedMotion();
    const [displayed, setDisplayed] = useState('');
    const [showCursor, setShowCursor] = useState(true);
    // Briefly shows the last command in the badge ("$ cd ~/articles") so the
    // navigation echo reads even while the panel is closed. Clears after a beat.
    const [echo, setEcho] = useState('');
    const echoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [termOpen, setTermOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef<HTMLDivElement>(null);
    const moreTriggerRef = useRef<HTMLButtonElement>(null);
    const terminalTriggerRef = useRef<HTMLButtonElement>(null);
    const [input, setInput] = useState('');
    const [lines, setLines] = useState<TerminalLine[]>([
        { type: 'output', text: 'Abhijith on AI — type "help" for commands' },
    ]);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // The tools menu closes on an outside click or Escape, never on mouseleave:
    // the panel is absolutely positioned, so it does not extend the wrapper's
    // box, and leaving the trigger to reach the panel would dismiss it.
    useEffect(() => {
        if (!moreOpen) return;
        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            e.preventDefault();
            setMoreOpen(false);
            moreTriggerRef.current?.focus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [moreOpen]);

    // A route change should not leave the menu hanging open.
    useEffect(() => { setMoreOpen(false); setTermOpen(false); }, [location.pathname]);

    // Typing animation
    useEffect(() => {
        if (reducedMotion) { setDisplayed(SITE_TITLE); return; }
        let i = 0;
        const interval = setInterval(() => {
            if (i < SITE_TITLE.length) {
                setDisplayed(SITE_TITLE.slice(0, i + 1));
                i++;
            } else clearInterval(interval);
        }, 80);
        return () => clearInterval(interval);
    }, [reducedMotion]);

    // Blinking cursor
    useEffect(() => {
        if (reducedMotion) { setShowCursor(true); return; }
        const blink = setInterval(() => setShowCursor(c => !c), 530);
        return () => clearInterval(blink);
    }, [reducedMotion]);

    // Clear a pending echo reset on unmount so it never fires on a dead header.
    useEffect(() => () => { if (echoTimer.current) clearTimeout(echoTimer.current); }, []);

    // Focus input when terminal opens
    useEffect(() => {
        if (!termOpen) return;
        const timer = setTimeout(() => inputRef.current?.focus(), 50);
        return () => clearTimeout(timer);
    }, [termOpen]);

    const isActive = (path: string) => {
        const current = location.pathname;
        if (path === '/articles') return current === path || current.startsWith('/article/');
        return current === path || (path !== '/' && current.startsWith(`${path}/`));
    };

    // Clicking a nav item is a `cd` — the terminal acknowledges the command by
    // echoing it, then the route follows. The chrome is the brand, so the
    // navigation makes the terminal literal rather than just looking like one.
    const goWithEcho = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const label = path === '/' ? '~' : `~${path}`;
        const cmd = `cd ${label}`;
        setLines(prev => [
            ...prev.slice(-6), // keep the panel short — it's an echo, not a log
            { type: 'input', text: `guest@abhijith:~$ ${cmd}` },
            { type: 'output', text: '' },
        ]);
        // Flash the command in the badge, then let the typed title take over again.
        setEcho(cmd);
        if (echoTimer.current) clearTimeout(echoTimer.current);
        echoTimer.current = setTimeout(() => setEcho(''), 1400);
        setMoreOpen(false);
    };

    const handleCommand = (cmd: string) => {
        const trimmed = cmd.trim().toLowerCase();
        const newLines: TerminalLine[] = [
            ...lines,
            { type: 'input', text: `guest@abhijith:~$ ${cmd}` },
        ];

        if (trimmed === 'clear') {
            setLines([{ type: 'output', text: 'Abhijith on AI — type "help" for commands' }]);
            setInput('');
            return;
        }

        // Handle cd commands
        if (trimmed.startsWith('cd ')) {
            const dir = trimmed.slice(3).replace(/\/$/, '');
            const route = CD_ROUTES[dir];
            if (route) {
                setLines([...newLines, { type: 'output', text: `Navigating to ~/${route.label}...` }]);
                setTimeout(() => { navigate(route.path); setTermOpen(false); }, 400);
            } else {
                setLines([...newLines, { type: 'output', text: `bash: cd: ${dir}: No such file or directory` }]);
            }
            setInput('');
            return;
        }

        if (trimmed in COMMANDS) {
            setLines([...newLines, { type: 'output', text: COMMANDS[trimmed] }]);
        } else if (trimmed === '') {
            setLines(newLines);
        } else {
            setLines([...newLines, { type: 'output', text: `bash: ${trimmed}: command not found` }]);
        }
        setInput('');
    };

    return (
        <>
            <nav className="terminal-header" aria-label="Main navigation" style={{
                // A nearly solid surface keeps the nav readable over page media.
                backgroundColor: NAV.bg,
                backdropFilter: 'blur(22px)',
                WebkitBackdropFilter: 'blur(22px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.09), 0 8px 28px -12px rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 24px',
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                borderBottom: `1px solid ${NAV.rule}`,
            }}>
                {/* Terminal badge */}
                <button
                    ref={terminalTriggerRef}
                    type="button"
                    className="terminal-badge"
                    onClick={() => setTermOpen(o => !o)}
                    aria-label="Toggle the terminal"
                    aria-expanded={termOpen}
                    aria-controls="header-terminal"
                    style={{
                        backgroundColor: NAV.inset,
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        border: `1px solid ${NAV.rule}`,
                        borderRadius: '9px',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                        padding: '5px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = NAV.ruleHi)}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = NAV.rule)}
                >
                    {/* The host carries the accent; the typed title is ink. */}
                    <span style={{ color: NAV.muted }}>guest</span>
                    <span style={{ color: NAV.faint }}> @ </span>
                    <span style={{ color: NAV.accent, fontWeight: 600 }}>abhijith</span>
                    <span style={{ color: NAV.faint }}> : ~ $ </span>
                    {/* While a nav command is fresh, the badge shows the command
                        itself; otherwise it types out the site title. */}
                    <span style={{ color: echo ? NAV.accent : NAV.ink, fontWeight: '600', transition: 'color 0.2s' }}>
                        {echo || displayed}
                    </span>
                    <span style={{
                        display: 'inline-block', width: '8px', height: '15px',
                        backgroundColor: NAV.accent, opacity: showCursor ? 0.85 : 0,
                        transition: 'opacity 0.05s', marginLeft: '1px',
                    }} aria-hidden="true" />
                </button>

                {/* Right side: nav + search + theme toggle */}
                <div className="header-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {PRIMARY_NAV.map(({ label, path }) => (
                        <Link
                            key={path}
                            to={path}
                            className="header-nav__item tap-hit"
                            aria-current={isActive(path) ? 'page' : undefined}
                            onClick={event => goWithEcho(event, path)}
                            style={navItemStyle(isActive(path), NAV)}
                            onMouseEnter={e => (e.currentTarget.style.color = NAV.strong)}
                            onMouseLeave={e => (e.currentTarget.style.color = isActive(path) ? NAV.ink : NAV.muted)}
                        >
                            <span className="header-nav__prefix">~/</span>{label}
                        </Link>
                    ))}

                    {/* The tools live behind a disclosure rather than as three more
                        top-level links — seven equal items read as a list, not a
                        hierarchy, and the two the site is actually built around
                        (writing and labs) were losing against them. */}
                    <div className="nav-more" ref={moreRef} onBlur={event => {
                        if (!event.currentTarget.contains(event.relatedTarget)) setMoreOpen(false);
                    }}>
                        <button
                            ref={moreTriggerRef}
                            type="button"
                            className="header-nav__item nav-more__trigger tap-hit"
                            aria-expanded={moreOpen}
                            aria-controls="header-tools"
                            onClick={() => setMoreOpen(o => !o)}
                            style={navItemStyle(SECONDARY_NAV.some(n => isActive(n.path)), NAV)}
                        >
                            <span className="header-nav__prefix">~/</span>TOOLS
                            <span className={`nav-more__caret${moreOpen ? ' is-open' : ''}`} aria-hidden>
                                <svg width="9" height="9" viewBox="0 0 16 16" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3.5 6l4.5 4.5L12.5 6" />
                                </svg>
                            </span>
                        </button>

                        <div id="header-tools" className={`nav-more__panel glass glass--quiet${moreOpen ? ' is-open' : ''}`} hidden={!moreOpen}>
                            {SECONDARY_NAV.map(({ label, path, note }, i) => (
                                <Link
                                    key={path}
                                    to={path}
                                    aria-current={isActive(path) ? 'page' : undefined}
                                    className="nav-more__item"
                                    style={{ transitionDelay: moreOpen ? `${40 + i * 45}ms` : '0ms' }}
                                    onClick={event => goWithEcho(event, path)}
                                >
                                    <span
                                        className="nav-more__dot"
                                        style={{ background: isActive(path) ? NAV.accent : 'transparent' }}
                                    />
                                    <span className="nav-more__label">{label}</span>
                                    <span className="nav-more__note">{note}</span>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {TRAILING_NAV.map(({ label, path }) => (
                        <Link
                            key={path}
                            to={path}
                            className="header-nav__item tap-hit"
                            aria-current={isActive(path) ? 'page' : undefined}
                            onClick={event => goWithEcho(event, path)}
                            style={navItemStyle(isActive(path), NAV)}
                            onMouseEnter={e => (e.currentTarget.style.color = NAV.strong)}
                            onMouseLeave={e => (e.currentTarget.style.color = isActive(path) ? NAV.ink : NAV.muted)}
                        >
                            <span className="header-nav__prefix">~/</span>{label}
                        </Link>
                    ))}

                    <HeaderSearch />

                    {/* Single cycling theme button: click to cycle light → dark → system */}
                    {setMode && (() => {
                        const cycle: { value: ThemeMode; icon: React.ReactNode; title: string }[] = [
                            { value: 'light',  icon: <Sun />,         title: 'Light mode — click for dark' },
                            { value: 'dark',   icon: <Moon />,        title: 'Dark mode — click for system' },
                            { value: 'system', icon: <SystemTheme />, title: 'System default — click for light' },
                        ];
                        const current = cycle.find(c => c.value === mode) || cycle[2];
                        const nextMode = cycle[(cycle.indexOf(current) + 1) % cycle.length].value;
                        return (
                            <button
                                type="button"
                                className="header-theme tap-hit"
                                onClick={() => setMode(nextMode)}
                                title={current.title}
                                aria-label={current.title}
                                style={{
                                    background: NAV.inset,
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    border: `1px solid ${NAV.rule}`,
                                    borderRadius: '9px',
                                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                                    display: 'grid', placeItems: 'center',
                                    width: '30px', height: '28px', padding: 0,
                                    cursor: 'pointer',
                                    color: NAV.accent,
                                    transition: 'border-color 0.15s',
                                    fontFamily: 'inherit',
                                    lineHeight: 1,
                                }}
                                onMouseEnter={e => (e.currentTarget.style.borderColor = NAV.ruleHi)}
                                onMouseLeave={e => (e.currentTarget.style.borderColor = NAV.rule)}
                            >
                                {current.icon}
                            </button>
                        );
                    })()}
                </div>
            </nav>

            {/* Dropdown terminal */}
            {termOpen && (
                <div
                    id="header-terminal"
                    style={{
                        position: 'fixed', top: '48px', left: '16px',
                        width: '520px', maxWidth: 'calc(100vw - 32px)',
                        backgroundColor: 'var(--bs-raised)',
                        border: `1px solid ${NAV.rule}`, borderTop: 'none',
                        zIndex: 999, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                        fontSize: '13px', boxShadow: '0 18px 44px -12px rgba(0,0,0,0.65)',
                        overflow: 'hidden',
                    }}
                    onClick={() => inputRef.current?.focus()}
                >
                    <div style={{ padding: '12px 16px', maxHeight: '200px', overflowY: 'auto' }}>
                        {lines.map((line, i) => (
                            <div key={i} style={{
                                color: line.type === 'input' ? NAV.strong : NAV.muted,
                                lineHeight: '1.6', whiteSpace: 'pre-wrap',
                            }}>
                                {line.text}
                            </div>
                        ))}
                    </div>
                    <div style={{
                        display: 'flex', alignItems: 'center', padding: '8px 16px',
                        borderTop: `1px solid ${NAV.rule}`, gap: '8px',
                    }}>
                        <span style={{ color: NAV.accent, whiteSpace: 'nowrap' }}>guest@abhijith:~$</span>
                        <input
                            ref={inputRef}
                            aria-label="Terminal command"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                                if (e.key === 'Enter') handleCommand(input);
                                if (e.key === 'Escape') {
                                    setTermOpen(false);
                                    terminalTriggerRef.current?.focus();
                                }
                            }}
                            style={{
                                flex: 1, background: 'none', border: 'none', outline: 'none',
                                color: NAV.strong, fontFamily: 'inherit', fontSize: '13px',
                                caretColor: NAV.accent,
                            }}
                            placeholder="type a command..."
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </div>
                </div>
            )}

            {termOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 998 }}
                    onClick={() => setTermOpen(false)} />
            )}
        </>
    );
};

export default TerminalHeader;
